import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { Clipboard } from "@capacitor/clipboard";
import { Capacitor } from "@capacitor/core";

import { collection, query, where, getDocs } from "firebase/firestore";

import Layout from "../components/layout/layout";
import OpenInAppBanner from "../components/layout/OpenInAppBanner";
import { D1, H1, P2, P3 } from "../components/theme/text/Headings";
import { Black, White, White08 } from "../components/theme/colors";
// Icons no longer needed here; Big 3 uses shared hook-provided icons

import { db } from "../services/firebase.config";
import mixpanelService from "../services/MixpanelService";
import postHogService from "../services/PostHogService";
import {
  ProPicGradient,
  SecPicGradient,
} from "../components/theme/ProfilePics";
import {
  Summary,
  ProfileOverviewInsights,
  FriendsModal,
  InsightsText,
  InfoBox,
  FriendBox,
  TraitsContainer,
  TraitItem,
  CopyLinkButton,
} from "../components/profile";

// Use shared InfoBox, FriendBox, TraitsContainer, TraitItem from components/profile

const ErrorBox = styled.div`
  border-radius: 10px;
  padding: 40px 20px;
  margin: 20px 0;
  width: 100%;
  display: flex;
  text-align: center;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background: #f8f9fa;
  border: 1px solid #e7e7e7;
`;

const PublicProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [userNotFound, setUserNotFound] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const profileInsightsRef = useRef(null);

  // Check if we're on browser (not native app)
  const isOnBrowser = !Capacitor.isNativePlatform();

  const { big3, big3Loading } = require("../hooks/profile").useBig3Traits(
    profileData?.id
  );

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!username) {
        setUserNotFound(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setUserNotFound(false);

        // Query for user by username (case insensitive)
        const usersQuery = query(
          collection(db, "public-profiles"),
          where("username", "==", username.toLowerCase())
        );

        const querySnapshot = await getDocs(usersQuery);

        if (querySnapshot.empty) {
          setUserNotFound(true);
        } else {
          // Get the first matching user
          const userDoc = querySnapshot.docs[0];
          const userData = userDoc.data();

          setProfileData({
            id: userDoc.id,
            ...userData,
            quizScore: Array.isArray(userData.shared)
              ? userData.shared.length
              : 0,
            friendsCount: Array.isArray(userData.friends)
              ? userData.friends.length
              : 0,
          });
        }
      } catch (error) {
        setUserNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [username]);

  // Custom Big 3 fetching removed; using shared hook above

  // Copy profile link to clipboard
  const handleCopyLink = async () => {
    if (!username) {
      console.log("No username available");
      return;
    }

    // Use /p/ prefix for shareable links (shows rich preview in iMessage, then redirects to profile)
    const profileLink = `https://www.vibecheckme.com/p/${username.toLowerCase()}`;
    console.log("Attempting to copy link:", profileLink);

    try {
      // Try Capacitor Clipboard first (works on native and web)
      await Clipboard.write({
        string: profileLink,
      });

      console.log("Link copied successfully!");
      setLinkCopied(true);

      // Track copy link event
      const copyLinkProps = {
        profile_user_id: profileData?.id,
        username: username,
        location: "public_profile_page",
        is_browser: isOnBrowser,
        timestamp: new Date().toISOString(),
      };
      mixpanelService.track("Public Profile Link Copied", copyLinkProps);
      postHogService.track("Public Profile Link Copied", copyLinkProps);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setLinkCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
      // Show alert as fallback
      alert(`Copy this link:\n${profileLink}`);
    }
  };

  if (loading) {
    return (
      <Layout
        $background={Black}
        $noNavbar={isOnBrowser}
        $noFooter={isOnBrowser}
      >
        <OpenInAppBanner />
        <Helmet>
          <title>Loading... - Vibe Check</title>
        </Helmet>
        <Container style={{ paddingTop: isOnBrowser ? "40px" : "0" }}>
          <Row>
            <Col
              lg={{ offset: 4, span: 4 }}
              md={{ offset: 3, span: 6 }}
              sm={{ offset: 2, span: 8 }}
              xs={{ offset: 1, span: 10 }}
              className="text-align-center"
            >
              <P3 color={White08} className="mt-50">
                Loading profile...
              </P3>
            </Col>
          </Row>
        </Container>
      </Layout>
    );
  }

  if (userNotFound) {
    return (
      <Layout
        $background={Black}
        $noNavbar={isOnBrowser}
        $noFooter={isOnBrowser}
      >
        <OpenInAppBanner />
        <Helmet>
          <title>User Not Found - Vibe Check</title>
          <meta
            name="description"
            content="This user profile could not be found on Vibe Check."
          />
        </Helmet>
        <Container style={{ paddingTop: isOnBrowser ? "40px" : "0" }}>
          <Row>
            <Col
              lg={{ offset: 4, span: 4 }}
              md={{ offset: 3, span: 6 }}
              sm={{ offset: 2, span: 8 }}
              xs={{ offset: 1, span: 10 }}
              className="text-align-center"
            >
              <ErrorBox>
                <H1 className="mb-10">User Not Found</H1>
                <P3 className="mb-20">
                  Sorry, we couldn't find a user with the username "@{username}
                  ".
                </P3>
                <button
                  onClick={() => navigate("/")}
                  style={{
                    background: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 20px",
                    cursor: "pointer",
                  }}
                >
                  Go to Home
                </button>
              </ErrorBox>
            </Col>
          </Row>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout $background={Black} $noNavbar={isOnBrowser} $noFooter={isOnBrowser}>
      <OpenInAppBanner />
      <Helmet>
        <title>
          {profileData?.displayName
            ? `${profileData.displayName}'s Profile - Vibe Check`
            : "Profile - Vibe Check"}
        </title>
        <meta
          name="description"
          content={
            profileData?.displayName
              ? `Check out ${profileData.displayName}'s vibe on Vibe Check. ${
                  profileData.quizScore || 0
                } quiz scores, ${profileData.friendsCount || 0} friends.`
              : "View profile on Vibe Check"
          }
        />
        {/* Open Graph tags for Facebook, LinkedIn, etc. */}
        <meta
          property="og:title"
          content={
            profileData?.displayName
              ? `${profileData.displayName}'s Profile`
              : "Profile - Vibe Check"
          }
        />
        <meta
          property="og:description"
          content={
            profileData?.displayName
              ? `Check out ${profileData.displayName}'s vibe on Vibe Check`
              : "View profile on Vibe Check"
          }
        />
        <meta property="og:type" content="profile" />
        <meta
          property="og:url"
          content={`https://www.vibecheckme.com/${username}`}
        />
        {profileData?.photoURL && (
          <meta property="og:image" content={profileData.photoURL} />
        )}
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta
          name="twitter:title"
          content={
            profileData?.displayName
              ? `${profileData.displayName} (@${profileData.username})`
              : "Profile - Vibe Check"
          }
        />
        <meta
          name="twitter:description"
          content={
            profileData?.displayName
              ? `Check out ${profileData.displayName}'s vibe on Vibe Check`
              : "View profile on Vibe Check"
          }
        />
        {profileData?.photoURL && (
          <meta name="twitter:image" content={profileData.photoURL} />
        )}
      </Helmet>
      <Container style={{ paddingTop: isOnBrowser ? "60px" : "0" }}>
        <Row>
          <Col xs={{ span: 3 }} className="ml-15">
            {profileData?.photoURL ? (
              <ProPicGradient src={profileData.photoURL} />
            ) : (
              <SecPicGradient>
                <D1 style={{ margin: 0 }}>
                  {profileData?.displayName ? profileData.displayName[0] : "?"}
                </D1>
              </SecPicGradient>
            )}
          </Col>
          <Col xs={{ span: 7 }} className="ml-10">
            <InfoBox>
              <div style={{ display: "flex", alignItems: "center" }}>
                <H1 className="mb-0">
                  {profileData?.displayName
                    ? profileData.displayName
                    : "Your Name"}
                </H1>
                {profileData?.username && (
                  <CopyLinkButton
                    onClick={handleCopyLink}
                    className={linkCopied ? "copied" : ""}
                    title={linkCopied ? "Copied!" : "Copy profile link"}
                  >
                    {linkCopied ? (
                      <CheckIcon style={{ fontSize: 18 }} />
                    ) : (
                      <ContentCopyIcon style={{ fontSize: 18 }} />
                    )}
                  </CopyLinkButton>
                )}
              </div>
              <P3 className="mt-0 mb-10">
                {profileData?.username
                  ? `@${profileData.username}`
                  : "no username"}
              </P3>

              <FriendBox>
                <div
                  className="display-column mb-0 mr-20"
                  role="button"
                  onClick={() => {
                    if (isOnBrowser) {
                      // Redirect to App Store on browser
                      window.location.href =
                        "https://apps.apple.com/app/vibe-check-compatibility-quiz/id6755138167";
                    } else {
                      setShowFriendsModal(true);
                    }
                  }}
                >
                  <H1 className="text-align-center">
                    {profileData?.friendsCount || 0}
                  </H1>
                  <P2>Friends</P2>
                </div>
                <div className="display-column mb-0 ml-10">
                  <H1 className="text-align-center">
                    {profileData?.quizScore || 0}
                  </H1>
                  <P2>Quiz Score</P2>
                </div>
              </FriendBox>
            </InfoBox>
          </Col>

          <InsightsText>
            <P2 bold>{profileData?.displayName}'s Vibe</P2>
            <ProfileOverviewInsights
              ref={profileInsightsRef}
              userId={profileData?.id}
              displayName={profileData?.displayName}
              onRegenerateComplete={() => {}}
            />

            {/* Big 3 traits under bio */}
            {big3Loading ? (
              <P2 color={White} className="mt-10" style={{ margin: 0 }}>
                Loading traits...
              </P2>
            ) : big3 ? (
              (() => {
                const traits = [
                  big3.personality,
                  big3.political,
                  big3.thinking,
                ].filter(Boolean);
                if (traits.length === 0) {
                  return (
                    <P3 color={White} className="mt-10">
                      No traits to show
                    </P3>
                  );
                }
                return (
                  <TraitsContainer>
                    {traits.map((trait, index) => {
                      const IconComponent = trait.icon;
                      return (
                        <TraitItem key={index}>
                          <IconComponent
                            style={{ fontSize: 15, color: White }}
                          />
                          <P3 color={White}>{trait.label}</P3>
                        </TraitItem>
                      );
                    })}
                  </TraitsContainer>
                );
              })()
            ) : null}
          </InsightsText>
        </Row>

        <Summary
          userId={profileData?.id}
          displayName={profileData?.displayName}
          isPublicProfile={true}
          profileInsightsRef={profileInsightsRef}
        />
      </Container>
      <FriendsModal
        show={showFriendsModal}
        onClose={() => setShowFriendsModal(false)}
        userId={profileData?.id}
        title="Friends"
      />
    </Layout>
  );
};

export default PublicProfile;
